"use client";

import { adjustNavbarScrollOffset } from "@/lib/navbar-scroll-offset";

import { Button } from "@/components/ui/button";
import { CornerDownRight } from "lucide-react";
import React, { useCallback } from "react";
import { cn } from "@/lib/utils";

interface Exam {
  type: string;
  value: string;
  /** 특정 문제로 직접 이동하려면 problemId를 지정합니다. 없으면 examSession으로 검색합니다. */
  problemId?: string;
}

interface RelatedExamsProps {
  /**
   * 관련된 시험 정보 배열입니다.
   * @example
   * [
   *   { type: "실기", value: "25년2회" },
   *   { type: "필기", value: "21년1회" },
   * ]
   */
  exams: Exam[];
  className?: string;
}

const RelatedExams: React.FC<RelatedExamsProps> = ({ exams, className }) => {
  // examSession 형식으로 변환 (예: { type: "실기", value: "25년2회" } -> "[실기]25년2회")
  const formatExamSession = useCallback((exam: Exam): string => {
    return `[${exam.type}]${exam.value}`;
  }, []);

  // 요소로 스크롤하고 하이라이트 효과 적용
  const scrollToElement = useCallback((element: Element) => {
    const elementTop = element.getBoundingClientRect().top + window.scrollY;
    const offset = adjustNavbarScrollOffset(100, elementTop); // navbar 높이 등을 고려한 오프셋

    window.scrollTo({
      top: elementTop - offset,
      behavior: "smooth"
    });

    // 하이라이트 효과 추가
    element.classList.add("ring-2", "ring-red-400", "ring-offset-2");
    setTimeout(() => {
      element.classList.remove("ring-2", "ring-red-400", "ring-offset-2");
    }, 2000);
  }, []);

  // 해당 exam session을 가진 문제로 스크롤
  const scrollToProblem = useCallback((exam: Exam) => {
    // problemId가 지정되어 있으면 해당 ID로 직접 스크롤
    if (exam.problemId) {
      const targetElement = document.getElementById(`problem-${exam.problemId}`);
      if (targetElement) {
        scrollToElement(targetElement);
        return;
      }
    }

    // problemId가 없거나 찾지 못한 경우 examSession으로 검색
    const targetSession = formatExamSession(exam);

    // data-exam-sessions 속성을 가진 모든 문제 요소 검색
    const problemElements = document.querySelectorAll("[data-exam-sessions]");

    for (const element of problemElements) {
      const examSessionsAttr = element.getAttribute("data-exam-sessions");
      if (!examSessionsAttr) continue;

      try {
        const examSessions: string[] = JSON.parse(examSessionsAttr);
        // examSessions는 "[실기]25년1회" 형식이므로 직접 비교
        if (examSessions.some(session => session.includes(targetSession))) {
          scrollToElement(element);
          return;
        }
      } catch {
        // JSON 파싱 실패 시 무시
        continue;
      }
    }
  }, [formatExamSession, scrollToElement]);

  if (!exams || exams.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 exam-related-tests m-1",
        className
      )}
      data-llm-copy-skip="true"
    >
      <CornerDownRight className="size-4 shrink-0" />
      <Button
        variant="destructive"
        size="sm"
        className="h-auto cursor-default gap-1.5"
      >
        {" "}
        출제
      </Button>
      {exams.map(({ type, value, problemId }, index) => (
        <Button
          key={index}
          variant="outline"
          size="sm"
          className="h-auto gap-1.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          onClick={() => scrollToProblem({ type, value, problemId })}
        >
          <div className="bg-secondary text-secondary-foreground text-base rounded-sm px-1.5 py-0.5 my-1 font-semibold">
            {type}
          </div>
          <span className="text-base font-semibold text-secondary-foreground">
            {value}
          </span>
        </Button>
      ))}
    </div>
  );
};

export default RelatedExams;
